import React, { createContext, useContext, useState, useEffect } from 'react';

export type PortalLanguage = 'fr' | 'en' | 'ar';

interface StudentPortalLanguageContextType {
  language: PortalLanguage;
  setLanguage: (lang: PortalLanguage) => void;
  t: (key: string, ...args: any[]) => string;
  dir: 'ltr' | 'rtl';
}

const translations: Record<PortalLanguage, Record<string, string>> = {
  fr: {
    'portal.student.brand': 'Blobtic Academy',
    'portal.student.title': 'Portail Étudiant & Parent',
    'portal.student.subtitle': 'Consultez votre planning, assiduité, groupes et situation financière',
    'portal.student.login_label': 'Identifiant (Email, Téléphone ou Code élève)',
    'portal.student.login_placeholder': 'Ex: STD-0001 ou etudiant@mail.com',
    'portal.student.password_label': 'Mot de passe',
    'portal.student.password_placeholder': 'Votre mot de passe',
    'portal.student.btn_login': 'Se connecter au portail',
    'portal.student.or_sso': 'Ou connectez-vous avec',
    'portal.student.sso_google': 'Continuer avec Google',
    'portal.student.sso_microsoft': 'Continuer avec Microsoft',
    'portal.student.select_academy_title': 'Choisissez votre Académie',
    'portal.student.select_academy_desc': 'Votre compte est inscrit dans plusieurs établissements. Sélectionnez celui auquel vous souhaitez accéder :',
    'portal.student.btn_access_academy': 'Accéder à cette académie',
    'portal.student.logout': 'Déconnexion',
    'portal.student.adult_badge': 'Espace Étudiant Adulte',
    'portal.student.parent_badge': 'Espace Parent & Famille',
    'portal.student.child_tab_label': 'Élève : {0}',
    'portal.student.disruptions_alert_title': 'Avis & Alertes de séances importantes',
    'portal.student.disruption_cancelled': 'Séance du {0} ({1} - {2}) : ANNULÉE. Motif : {3}',
    'portal.student.disruption_postponed': 'Séance du {0} : REPORTÉE au {1} à {2}. Motif : {3}',
    'portal.student.disruption_teacher_absent': 'Séance du {0} ({1} - {2}) : Professeur absent.',
    'portal.student.tab_overview': 'Vue d\'ensemble',
    'portal.student.tab_schedule': 'Emploi du temps',
    'portal.student.tab_attendance': 'Assiduité & Présences',
    'portal.student.tab_finances': 'Finances & Règlements',
    'portal.student.tab_profile': 'Fiche Informations',
    'portal.student.groups_title': 'Groupes & Formations suivies',
    'portal.student.upcoming_sessions_title': 'Prochaines séances de cours',
    'portal.student.no_upcoming_sessions': 'Aucune séance programmée pour le moment.',
    'portal.student.attendance_rate': 'Taux d\'assiduité',
    'portal.student.attendance_present': 'Présent',
    'portal.student.attendance_absent': 'Absent',
    'portal.student.attendance_late': 'En retard',
    'portal.student.attendance_excused': 'Excusé',
    'portal.student.justified': 'Justifié',
    'portal.student.unjustified': 'Non justifié',
    'portal.student.total_invoiced': 'Total facturé',
    'portal.student.total_paid': 'Total réglé',
    'portal.student.remaining_balance': 'Solde restant dû',
    'portal.student.financial_hold_warning': 'Attention : Un blocage financier temporaire est actif sur ce dossier. Veuillez contacter l\'administration.',
    'portal.student.info_birth': 'Date de naissance',
    'portal.student.info_phone': 'Téléphone',
    'portal.student.info_email': 'Email',
    'portal.student.info_emergency': 'Contact urgence',
    'portal.student.info_level': 'Niveau scolaire',
    'portal.student.info_guardian': 'Tuteur légal',
    'portal.student.change_academy': 'Changer d\'académie',
    'portal.student.welcome': 'Bonjour, {0}',
    'portal.student.date_label': 'Date',
    'portal.student.time_label': 'Horaire',
    'portal.student.group_label': 'Groupe',
    'portal.student.room_label': 'Salle',
    'portal.student.teacher_label': 'Enseignant',
    'portal.student.status_label': 'Statut',
    'portal.student.financial_hold_title': 'Blocage financier actif',
    'portal.student.default_reason_admin': 'Raison administrative',
    'portal.student.default_reason_pedagogic': 'Raison pédagogique',
    'portal.student.soon': 'prochainement',
    'portal.student.presences_out_of_sessions': '{0} présences / {1} séances',
    'portal.student.scheduled_sessions': 'Séances programmées',
    'portal.student.paid_out_of_invoiced': 'Payé : {0} / {1} DZD',
    'portal.student.course_fallback': 'Cours',
    'portal.student.assigned_room_fallback': 'Salle assignée',
    'portal.student.teacher_fallback': 'Professeur',
    'portal.student.teacher_short_label': 'Prof',
    'portal.student.session_id_col': 'Séance / ID',
    'portal.student.attendance_status_col': 'Statut Présence',
    'portal.student.checkin_time_col': 'Heure de pointage',
    'portal.student.method_col': 'Méthode',
    'portal.student.justification_col': 'Justification',
    'portal.student.no_attendance_records': 'Aucun enregistrement d\'assiduité disponible.',
    'portal.student.session_item_prefix': 'Séance #{0}',
    'portal.student.manual_scan_type': 'MANUEL',
    'portal.student.financial_history_title': 'Historique des factures et paiements',
    'portal.student.invoice_number_col': 'Numéro / Réf',
    'portal.student.amount_col': 'Montant',
    'portal.student.no_financial_records': 'Aucun document financier enregistré.',
    'portal.student.status_paid': 'RÉGLÉ',
    'portal.student.status_unpaid': 'NON RÉGLÉ',
    'portal.student.status_partial': 'PARTIEL',
    'portal.student.parent_relation_fallback': 'Parent',
    'portal.student.child_fallback': 'Élève #{0}',
    'portal.student.btn_justify': 'Justifier',
    'portal.student.justify_absence_title': 'Justifier une absence',
    'portal.student.justify_absence_desc': 'Fournissez le motif et éventuellement un justificatif pour la séance sélectionnée :',
    'portal.student.justification_reason_label': 'Motif de l\'absence',
    'portal.student.justification_doc_label': 'Pièce jointe (facultatif)',
    'portal.student.btn_submit_justification': 'Transmettre la justification',
    'portal.student.justification_success': 'Votre justification a été transmise avec succès.',
    'portal.student.justification_error': 'Erreur lors de l\'envoi de la justification. Veuillez réessayer.',
    'portal.student.justification_saved': 'Justification enregistrée',
    'portal.student.cancel': 'Annuler',
    'portal.student.hub_link': 'Hub Applications',
    'portal.student.change_language': 'Changer de langue',
  },
  en: {
    'portal.student.brand': 'Blobtic Academy',
    'portal.student.title': 'Student & Parent Portal',
    'portal.student.subtitle': 'Check your timetable, attendance, enrolled groups and financial balance',
    'portal.student.login_label': 'Login ID (Email, Phone or Student Code)',
    'portal.student.login_placeholder': 'e.g. STD-0001 or student@mail.com',
    'portal.student.password_label': 'Password',
    'portal.student.password_placeholder': 'Your password',
    'portal.student.btn_login': 'Sign in to portal',
    'portal.student.or_sso': 'Or sign in with',
    'portal.student.sso_google': 'Continue with Google',
    'portal.student.sso_microsoft': 'Continue with Microsoft',
    'portal.student.select_academy_title': 'Select your Academy',
    'portal.student.select_academy_desc': 'Your account is enrolled in multiple institutions. Please choose the academy to access:',
    'portal.student.btn_access_academy': 'Access this academy',
    'portal.student.logout': 'Sign out',
    'portal.student.adult_badge': 'Adult Student Space',
    'portal.student.parent_badge': 'Parent & Family Space',
    'portal.student.child_tab_label': 'Student: {0}',
    'portal.student.disruptions_alert_title': 'Important Session Notices & Alerts',
    'portal.student.disruption_cancelled': 'Session on {0} ({1} - {2}): CANCELLED. Reason: {3}',
    'portal.student.disruption_postponed': 'Session on {0}: POSTPONED to {1} at {2}. Reason: {3}',
    'portal.student.disruption_teacher_absent': 'Session on {0} ({1} - {2}): Teacher is absent.',
    'portal.student.tab_overview': 'Overview',
    'portal.student.tab_schedule': 'Schedule',
    'portal.student.tab_attendance': 'Attendance',
    'portal.student.tab_finances': 'Finances & Invoices',
    'portal.student.tab_profile': 'Profile Info',
    'portal.student.groups_title': 'Enrolled Courses & Groups',
    'portal.student.upcoming_sessions_title': 'Upcoming Classes',
    'portal.student.no_upcoming_sessions': 'No upcoming sessions scheduled.',
    'portal.student.attendance_rate': 'Attendance Rate',
    'portal.student.attendance_present': 'Present',
    'portal.student.attendance_absent': 'Absent',
    'portal.student.attendance_late': 'Late',
    'portal.student.attendance_excused': 'Excused',
    'portal.student.justified': 'Justified',
    'portal.student.unjustified': 'Unjustified',
    'portal.student.total_invoiced': 'Total Invoiced',
    'portal.student.total_paid': 'Total Paid',
    'portal.student.remaining_balance': 'Remaining Due',
    'portal.student.financial_hold_warning': 'Notice: A temporary financial hold is active on this record. Please contact the administration.',
    'portal.student.info_birth': 'Date of Birth',
    'portal.student.info_phone': 'Phone',
    'portal.student.info_email': 'Email',
    'portal.student.info_emergency': 'Emergency Contact',
    'portal.student.info_level': 'School Level',
    'portal.student.info_guardian': 'Legal Guardian',
    'portal.student.change_academy': 'Switch Academy',
    'portal.student.welcome': 'Welcome, {0}',
    'portal.student.date_label': 'Date',
    'portal.student.time_label': 'Time',
    'portal.student.group_label': 'Group',
    'portal.student.room_label': 'Room',
    'portal.student.teacher_label': 'Teacher',
    'portal.student.status_label': 'Status',
    'portal.student.financial_hold_title': 'Active Financial Hold',
    'portal.student.default_reason_admin': 'Administrative reason',
    'portal.student.default_reason_pedagogic': 'Academic reason',
    'portal.student.soon': 'soon',
    'portal.student.presences_out_of_sessions': '{0} attendances / {1} sessions',
    'portal.student.scheduled_sessions': 'Scheduled sessions',
    'portal.student.paid_out_of_invoiced': 'Paid: {0} / {1} DZD',
    'portal.student.course_fallback': 'Course',
    'portal.student.assigned_room_fallback': 'Assigned room',
    'portal.student.teacher_fallback': 'Teacher',
    'portal.student.teacher_short_label': 'Teacher',
    'portal.student.session_id_col': 'Session / ID',
    'portal.student.attendance_status_col': 'Attendance Status',
    'portal.student.checkin_time_col': 'Check-in Time',
    'portal.student.method_col': 'Method',
    'portal.student.justification_col': 'Justification',
    'portal.student.no_attendance_records': 'No attendance records available.',
    'portal.student.session_item_prefix': 'Session #{0}',
    'portal.student.manual_scan_type': 'MANUAL',
    'portal.student.financial_history_title': 'Invoices & Payments History',
    'portal.student.invoice_number_col': 'Number / Ref',
    'portal.student.amount_col': 'Amount',
    'portal.student.no_financial_records': 'No financial documents recorded.',
    'portal.student.status_paid': 'PAID',
    'portal.student.status_unpaid': 'UNPAID',
    'portal.student.status_partial': 'PARTIAL',
    'portal.student.parent_relation_fallback': 'Parent',
    'portal.student.child_fallback': 'Student #{0}',
    'portal.student.btn_justify': 'Justify',
    'portal.student.justify_absence_title': 'Justify an Absence',
    'portal.student.justify_absence_desc': 'Provide the reason and optional attachment for the selected session:',
    'portal.student.justification_reason_label': 'Absence reason',
    'portal.student.justification_doc_label': 'Attachment (optional)',
    'portal.student.btn_submit_justification': 'Submit justification',
    'portal.student.justification_success': 'Your justification has been submitted successfully.',
    'portal.student.justification_error': 'Error submitting justification. Please try again.',
    'portal.student.justification_saved': 'Justification recorded',
    'portal.student.cancel': 'Cancel',
    'portal.student.hub_link': 'Applications Hub',
    'portal.student.change_language': 'Change language',
  },
  ar: {
    'portal.student.brand': 'أكاديمية بلوبتيك',
    'portal.student.title': 'بوابة الطالب وولي الأمر',
    'portal.student.subtitle': 'اطلع على جدول الحصص، الحضور والغياب، الأفواج والوضعية المالية',
    'portal.student.login_label': 'المعرّف (البريد الإلكتروني، الهاتف أو كود الطالب)',
    'portal.student.login_placeholder': 'مثال: STD-0001 أو etudiant@mail.com',
    'portal.student.password_label': 'كلمة المرور',
    'portal.student.password_placeholder': 'كلمة المرور الخاصة بك',
    'portal.student.btn_login': 'تسجيل الدخول إلى البوابة',
    'portal.student.or_sso': 'أو تسجيل الدخول عبر',
    'portal.student.sso_google': 'المتابعة بواسطة Google',
    'portal.student.sso_microsoft': 'المتابعة بواسطة Microsoft',
    'portal.student.select_academy_title': 'اختر الأكاديمية أو المركز',
    'portal.student.select_academy_desc': 'حسابك مسجل في أكثر من مؤسسة. يرجى اختيار المؤسسة التي ترغب في تصفحها:',
    'portal.student.btn_access_academy': 'الدخول إلى هذه المؤسسة',
    'portal.student.logout': 'تسجيل الخروج',
    'portal.student.adult_badge': 'فضاء الطالب الراشد',
    'portal.student.parent_badge': 'فضاء ولي الأمر والعائلة',
    'portal.student.child_tab_label': 'التلميذ: {0}',
    'portal.student.disruptions_alert_title': 'إشعارات وتنبيهات الحصص الهامة',
    'portal.student.disruption_cancelled': 'حصة يوم {0} ({1} - {2}): تم إلغاؤها. السبب: {3}',
    'portal.student.disruption_postponed': 'حصة يوم {0}: تم تأجيلها إلى {1} على الساعة {2}. السبب: {3}',
    'portal.student.disruption_teacher_absent': 'حصة يوم {0} ({1} - {2}): الأستاذ غائب.',
    'portal.student.tab_overview': 'نظرة عامة',
    'portal.student.tab_schedule': 'جدول الأوقات',
    'portal.student.tab_attendance': 'سجل الحضور والغياب',
    'portal.student.tab_finances': 'المستحقات والمدفوعات',
    'portal.student.tab_profile': 'بيانات الملف',
    'portal.student.groups_title': 'الأفواج والبرامج المسجلة',
    'portal.student.upcoming_sessions_title': 'الحصص الدراسية القادمة',
    'portal.student.no_upcoming_sessions': 'لا توجد حصص مبرمجة حالياً.',
    'portal.student.attendance_rate': 'نسبة الحضور',
    'portal.student.attendance_present': 'حاضر',
    'portal.student.attendance_absent': 'غائب',
    'portal.student.attendance_late': 'متأخر',
    'portal.student.attendance_excused': 'معذور',
    'portal.student.justified': 'مبرر',
    'portal.student.unjustified': 'غير مبرر',
    'portal.student.total_invoiced': 'إجمالي المفوتر',
    'portal.student.total_paid': 'إجمالي المدفوع',
    'portal.student.remaining_balance': 'المبلغ المتبقي',
    'portal.student.financial_hold_warning': 'تنبيه: يوجد تعليق مالي مؤقت على هذا الملف. يرجى مراجعة إدارة المؤسسة.',
    'portal.student.info_birth': 'تاريخ الميلاد',
    'portal.student.info_phone': 'الهاتف',
    'portal.student.info_email': 'البريد الإلكتروني',
    'portal.student.info_emergency': 'رقم الطوارئ',
    'portal.student.info_level': 'المستوى الدراسي',
    'portal.student.info_guardian': 'الولي الشرعي',
    'portal.student.change_academy': 'تغيير الأكاديمية',
    'portal.student.welcome': 'مرحباً، {0}',
    'portal.student.date_label': 'التاريخ',
    'portal.student.time_label': 'التوقيت',
    'portal.student.group_label': 'الفوج',
    'portal.student.room_label': 'القاعة',
    'portal.student.teacher_label': 'الأستاذ',
    'portal.student.status_label': 'الحالة',
    'portal.student.financial_hold_title': 'تعليق مالي نشط',
    'portal.student.default_reason_admin': 'سبب إداري',
    'portal.student.default_reason_pedagogic': 'سبب بيداغوجي',
    'portal.student.soon': 'قريباً',
    'portal.student.presences_out_of_sessions': '{0} حضور / {1} حصص',
    'portal.student.scheduled_sessions': 'حصص مبرمجة',
    'portal.student.paid_out_of_invoiced': 'المدفوع: {0} / {1} دج',
    'portal.student.course_fallback': 'درس',
    'portal.student.assigned_room_fallback': 'قاعة معينة',
    'portal.student.teacher_fallback': 'أستاذ',
    'portal.student.teacher_short_label': 'الأستاذ',
    'portal.student.session_id_col': 'الحصة / المعرف',
    'portal.student.attendance_status_col': 'حالة الحضور',
    'portal.student.checkin_time_col': 'وقت تسجيل الحضور',
    'portal.student.method_col': 'طريقة التسجيل',
    'portal.student.justification_col': 'التبرير',
    'portal.student.no_attendance_records': 'لا توجد أي تسجيلات حضور متاحة حالياً.',
    'portal.student.session_item_prefix': 'حصة رقم {0}',
    'portal.student.manual_scan_type': 'يدوي',
    'portal.student.financial_history_title': 'سجل الفواتير والمدفوعات',
    'portal.student.invoice_number_col': 'الرقم / المرجع',
    'portal.student.amount_col': 'المبلغ',
    'portal.student.no_financial_records': 'لا توجد أي وثائق مالية مسجلة.',
    'portal.student.status_paid': 'مسدد',
    'portal.student.status_unpaid': 'غير مسدد',
    'portal.student.status_partial': 'تسديد جزئي',
    'portal.student.parent_relation_fallback': 'ولي أمر',
    'portal.student.child_fallback': 'التلميذ رقم {0}',
    'portal.student.btn_justify': 'تقديم تبرير',
    'portal.student.justify_absence_title': 'تبرير غياب',
    'portal.student.justify_absence_desc': 'يرجى تقديم سبب الغياب مع إمكانية إرفاق وثيقة تبريرية للحصة المحددة:',
    'portal.student.justification_reason_label': 'سبب الغياب',
    'portal.student.justification_doc_label': 'وثيقة مرفقة (اختياري)',
    'portal.student.btn_submit_justification': 'إرسال التبرير',
    'portal.student.justification_success': 'تم إرسال تبرير الغياب بنجاح.',
    'portal.student.justification_error': 'حدث خطأ أثناء إرسال التبرير. يرجى المحاولة مرة أخرى.',
    'portal.student.justification_saved': 'تم تسجيل التبرير',
    'portal.student.cancel': 'إلغاء',
    'portal.student.hub_link': 'بوابة التطبيقات',
    'portal.student.change_language': 'تغيير اللغة',
  }
};

const StudentPortalLanguageContext = createContext<StudentPortalLanguageContextType | undefined>(undefined);

export const StudentPortalLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<PortalLanguage>(() => {
    return (localStorage.getItem('blobtic_portal_student_lang') as PortalLanguage) || 'fr';
  });

  const setLanguage = (lang: PortalLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('blobtic_portal_student_lang', lang);
  };

  const dir: 'ltr' | 'rtl' = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [dir, language]);

  const t = (key: string, ...args: any[]): string => {
    const text = translations[language]?.[key] || translations['fr']?.[key] || key;
    if (!args.length) return text;
    return text.replace(/\{(\d+)\}/g, (_match, index) => {
      const idx = parseInt(index, 10);
      return args[idx] !== undefined ? String(args[idx]) : '';
    });
  };

  return (
    <StudentPortalLanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      <div dir={dir} style={{ minHeight: '100vh', width: '100%' }}>
        {children}
      </div>
    </StudentPortalLanguageContext.Provider>
  );
};

export const useStudentPortalLanguage = (): StudentPortalLanguageContextType => {
  const context = useContext(StudentPortalLanguageContext);
  if (!context) {
    throw new Error('useStudentPortalLanguage must be used within a StudentPortalLanguageProvider');
  }
  return context;
};
